import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { format } from '@hokify/shared-components/lib/helpers/datehelpers/format';
import type { IAPIPageSettings, IAPIOtherTerms } from '@hokify/common';
import type { IAPIArticleTeaser, IAPIArticleAuthor, IAPIArticle } from '@hokify/magazine-interface';

import Breadcrumbs from '~/components/website/Breadcrumbs.vue';
import { getHeadFromPageSettings, getSocialMediaMetaData } from '~/helpers/metadata';
import { PageNotFoundError } from '~/errors/PageNotFoundError';
import { crumbsMaker } from '~/helpers/magazine';
import type { ISeoFooterData } from '~/components/website/footer/SeoFooter.vue';

const magazineStore = namespace('magazine');

@Component({
	components: {
		Breadcrumbs
	},
	head(this: Article) {
		const result: any = getHeadFromPageSettings(this.pageSettings);
		if (this.myArticle.title) {
			result.title = this.myArticle.titletag ? this.myArticle.titletag : this.myArticle.title;
			if (!result.title.toLowerCase().includes('hokify')) {
				result.titleTemplate = '%s | hokify';
			}
		}
		result.meta.push({
			hid: 'description',
			name: 'description',
			content: this.myArticle.metadesc
		});
		result.meta.push({
			hid: 'author',
			name: 'author',
			content: this.myArticle?.author?.displayName
		});
		result.meta.push(
			...getSocialMediaMetaData(
				result,
				this.$store.state.topLevelDomain,
				[this.myArticle.images?.medium],
				undefined,
				this.$route.query
			)
		);
		return result;
	},
	async asyncData({ store, query, params, route, error }) {
		const { alias } = params;
		if (!alias) {
			error({ statusCode: 404, message: 'Dieser Artikel existiert nicht.' });
			return;
		}
		const isPreview = !!query?.preview;

		try {
			const result = await store.dispatch('magazine/loadArticle', {
				alias,
				preview: isPreview,
				includeInterlinking: true
			});

			return {
				linkedArticles: result.linkedArticles,
				myArticle: result.article,
				pageSettings: result?.pageSettings || {},
				alias,
				facebookHref: `${store.state.host}${route.path}`,
				uref: undefined, // for later usage of reference like facebook, adwords, etc.
				cref: undefined, // for later usage of reference like facebook, adwords, etc.
				isPreview,
				otherTerms: result?.otherTerms
			};
		} catch (err: any) {
			if (err.response?.status === 404) {
				error(new PageNotFoundError('Der angegebene Artikel existiert nicht.', true));
			} else {
				error(err);
			}
		}
	}
})
export default class Article extends Vue {
	@magazineStore.Action('loadArticlesForCategory') loadArticlesForCategory;

	format = format;

	facebookHref!: string;

	alias!: string;

	pageSettings!: IAPIPageSettings;

	uref?: string;

	cref?: string;

	isPreview?: boolean;

	linkedArticles!: IAPIArticleTeaser[];

	otherTerms!: IAPIOtherTerms;

	// NOTE: do not delete this, otherwise purgecss might delete dynamically loaded classes
	// that are used in the article (that comes from the backend)
	allowedStylingClasses = [
		'float-left', // image
		'float-right', // image
		'w-1/3', // image
		'w-1/2', // image
		'w-2/3', // image
		'w-3/4', // image
		'w-full', // image
		'aspect-square', // image
		'aspect-4/3', // image
		'aspect-10/3', // image
		'mr-4', // image
		'ml-4', // image
		'mx-auto', // image
		'mb-2', // image
		'text-color-main', // text color
		'text-color-main-business', // text color
		'text-color-blue', // text color
		'bg-color-main-light' // highlight color
	];

	removeEventListeners() {
		window.removeEventListener('focus', this.triggerView, { capture: false });
		window.removeEventListener('keydown', this.triggerView, { capture: false });
		window.removeEventListener('mousemove', this.triggerView, { capture: false });
		window.removeEventListener('mousewheel', this.triggerView, { capture: false });
		window.removeEventListener('touchstart', this.triggerView, { capture: false });
	}

	triggerView() {
		this.removeEventListeners();

		this.$trackWebsiteEvent('article_view', { articleId: this.myArticle._id });
	}

	myArticle!: IAPIArticle;

	observer?: IntersectionObserver;

	showBar = false;

	get pageMode(): 'business' | 'press' | 'user' {
		if (this.$route.path.startsWith('/business')) {
			return 'business';
		}
		if (this.$route.path.startsWith('/press')) {
			return 'press';
		}
		return 'user';
	}

	crumbsMaker = crumbsMaker;

	get getCrumbs() {
		const crumbs = this.crumbsMaker(this.pageMode, this.myArticle, this.regionHasCoreArticle);
		if (!this.myArticle.isCoreArticle && this.$isMobile.phone) {
			// on mobile we remove the last crumb for spacing reasons
			crumbs.pop();
		}
		return crumbs;
	}

	get regionHasCoreArticle(): boolean {
		return !!(this.myArticle?.isCoreArticle || this.myArticle.category?.coreArticle?.isCoreArticle);
	}

	get author(): IAPIArticleAuthor | undefined {
		return this.myArticle.author;
	}

	get authorName(): string {
		return this.author?.displayName || 'hokify Team';
	}

	get formattedDate(): string {
		return format('dd.MM.yyyy', this.myArticle.date);
	}

	dateSchema(date): string {
		return format('yyyy-MM-dd', date);
	}

	get publisherLogo(): string {
		return require(`@hokify/shared-components/assets/svgs/hokify/logo-brand-white.svg`); // eslint-disable-line
	}

	get mappedSeoBranches():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.otherTerms?.interlinking?.jobField) {
			return this.otherTerms?.interlinking?.jobField.map(page => ({
				title: page.name,
				description: `${page.results}`,
				to: page.url
			}));
		}
		return undefined;
	}

	get mappedSeoRegions():
		| {
				title: string;
				description: string;
				to: string;
		  }[]
		| undefined {
		if (this.otherTerms?.interlinking?.locationHierarchy) {
			return this.otherTerms?.interlinking?.locationHierarchy.map(place => ({
				title: place.name,
				description: `${place.results}`,
				to: place.url
			}));
		}
		return undefined;
	}

	get mappedSeoRecentJobSearches():
		| {
				title: string;
				to: string;
		  }[]
		| undefined {
		if (this.otherTerms?.searches) {
			return this.otherTerms?.searches.map(search => ({
				title: search.name,
				to: search.url
			}));
		}
		return undefined;
	}

	get seoFooterData(): ISeoFooterData[] {
		return [
			{
				title: 'Jobs nach Regionen',
				id: 'regions',
				searches: this.mappedSeoRegions || []
			},
			{
				title: 'Jobs nach Branchen',
				id: 'branches',
				searches: this.mappedSeoBranches || []
			},
			{
				title: 'Aktuelle Suchen',
				id: 'current-searches',
				searches: this.mappedSeoRecentJobSearches || []
			}
		];
	}

	mounted() {
		this.registerIntersectionObserver();
		const options = {
			once: true,
			passive: true,
			capture: false
		};
		window.addEventListener('focus', this.triggerView, options);
		window.addEventListener('keydown', this.triggerView, options);
		window.addEventListener('mousemove', this.triggerView, options);
		window.addEventListener('mousewheel', this.triggerView, options);
		window.addEventListener('touchstart', this.triggerView, options);
	}

	beforeDestroy() {
		this.observer?.disconnect();
		this.removeEventListeners();
	}

	isVisible(entries) {
		this.showBar = !entries.some(entry => entry.isIntersecting);
	}

	registerIntersectionObserver() {
		this.observer = new IntersectionObserver(this.isVisible);
		this.observeElements();
	}

	observeElements() {
		const el1 = document.querySelector('#header');
		if (el1) {
			this.observer?.observe(el1);
		}
		const el2 = document.querySelector('#footer');
		if (el2) {
			this.observer?.observe(el2);
		}
	}

	downloadClicked(url: string) {
		this.$trackWebsiteEvent('download_start', { url, article: this.alias });
	}
}
